import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { customStaticRanges } from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import { enGB } from 'date-fns/locale';
import { FaChevronDown } from 'react-icons/fa';
import { useDatePickerPosition } from 'hooks/useDatePickerPosition';

// E-Invoice status options
export const eInvoiceStatusMap = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  Processing: 'Processing',
  Success: 'Success',
  Cancelled: 'Cancelled',
  Credited: 'Credited',
};

// Invoice type options
export const invoiceTypeMap = {
  B2C: 'B2C (Customer)',
  B2B: 'B2B (Merchant)',
};

const EInvoiceListFilter = ({
  region,
  setRegion,
  invoiceId,
  setInvoiceId,
  transactionId,
  setTransactionId,
  merchantName,
  setMerchantName,
  setPage,
  setShowCalendar,
  showCalendar,
  tempDateRange,
  handleApply,
  handleClear,
  handleSelect,
  calendarFilterTitle,
  handleReset,
  invoiceType,
}) => {
  const { datePickerButtonRef, getCalendarPosition } = useDatePickerPosition();
  const { regions } = useRegions();

  return (
    <>
      <Flex
        alignItems={'flex-end'}
        flexWrap={'wrap'}
        mb={4}
        gap={{ base: '14px', md: '20px' }}
      >
        {/* Region Filter */}
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={region ? 'green.500' : 'gray.400'}
          >
            Region
          </Text>
          <Select
            value={region}
            onChange={(e) => {
              setPage(1);
              setRegion(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          >
            <option value="">All</option>
            {regions?.map((regionItem) => (
              <option key={regionItem.id} value={regionItem.id}>
                {regionItem.label}
              </option>
            ))}
          </Select>
        </Box>

        {/* Invoice ID Filter */}
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={invoiceId ? 'green.500' : 'gray.400'}
          >
            Invoice ID
          </Text>
          <Input
            placeholder="Search Invoice ID..."
            value={invoiceId}
            onChange={(e) => {
              setInvoiceId(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          />
        </Box>

        {/* Merchant Name Filter */}
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={merchantName ? 'green.500' : 'gray.400'}
          >
            Merchant Name
          </Text>
          <Input
            placeholder="Search Merchant Name..."
            value={merchantName}
            onChange={(e) => {
              setMerchantName(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          />
        </Box>

        {/* Transaction ID Filter - Only for B2C */}
        {invoiceType === 'B2C' && (
          <Box width={{ base: '100%', md: 'auto' }}>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={transactionId ? 'green.500' : 'gray.400'}
            >
              Transaction ID
            </Text>
            <Input
              placeholder="Search Transaction ID..."
              value={transactionId}
              onChange={(e) => {
                setTransactionId(e.target.value);
              }}
              width={{ base: '100%', md: '200px' }}
              background={'#fff'}
              borderRadius="10px"
            />
          </Box>
        )}

        {/* Status Filter - Commented for now */}
        {/* <Flex
          gap={{ base: '10px', md: '20px' }}
          width={{ base: '100%', md: 'auto' }}
          alignItems={{ base: 'start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <label>Status:</label>
          <Select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          >
            <option value="">All</option>
            {Object.entries(eInvoiceStatusMap).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Select>
        </Flex> */}

        {/* Invoice Type Filter - Not needed, handled by tabs */}
        {/* <Flex
          gap={{ base: '10px', md: '20px' }}
          width={{ base: '100%', md: 'auto' }}
          alignItems={{ base: 'start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
        >
          <label>Invoice Type:</label>
          <Select
            value={invoiceType}
            onChange={(e) => {
              setPage(1);
              setInvoiceType(e.target.value);
            }}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          >
            <option value="">All</option>
            {Object.entries(invoiceTypeMap).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Select>
        </Flex> */}

        {/* Invoice Date Filter */}
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={
              calendarFilterTitle !== 'Select Date' ? 'green.500' : 'gray.400'
            }
          >
            Invoice Date
          </Text>
          <Box
            position="relative"
            flexShrink="0"
            width={{ base: '100%', md: '200px' }}
          >
            <Button
              ref={datePickerButtonRef}
              py="10px"
              px="15"
              fontWeight="semibold"
              type="button"
              bg="white"
              border="1px solid"
              borderColor="reviveGray"
              color="#292D32"
              borderRadius="8px"
              onClick={() => setShowCalendar(!showCalendar)}
              justifyContent="space-between"
              width={{ base: '100%', md: '200px' }}
            >
              {calendarFilterTitle}{' '}
              <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
            </Button>
            {/* Date Range Picker Dropdown */}
            {showCalendar && (
              <>
                <Box
                  position="fixed"
                  bg="rgb(0 0 0 / 40%)"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  zIndex="999"
                  onClick={() => setShowCalendar(false)}
                ></Box>
                <Box
                  p={{ base: '20px', md: '32px' }}
                  mt="8px"
                  position="fixed"
                  {...getCalendarPosition()}
                  zIndex="1000"
                  bg="white"
                  boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                  borderRadius="20px"
                  className="custom-date-picker"
                  maxWidth={{ base: '95vw', md: '900px' }}
                  width={{ base: '95vw', md: 'auto' }}
                  maxHeight="90vh"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  <DateRangePicker
                    ranges={tempDateRange}
                    onChange={handleSelect}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    locale={enGB}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                    staticRanges={customStaticRanges}
                    inputRanges={[]}
                  />
                  <Flex justifyContent="right" gap="12px" marginTop="30px">
                    <Button
                      className="clear-btn"
                      border="1px solid"
                      borderColor="reviveGray"
                      borderRadius="10px"
                      onClick={handleClear}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Clear
                    </Button>
                    <Button
                      className="apply-btn"
                      bg="reviveOrange"
                      color="white"
                      borderRadius="10px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={handleApply}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Reset Button */}
        <Flex gap={{ base: '14px', md: '20px' }} alignItems="flex-end">
          <Button onClick={handleReset}>Reset</Button>
        </Flex>
      </Flex>
    </>
  );
};

EInvoiceListFilter.propTypes = {
  region: PropTypes.string,
  setRegion: PropTypes.func,
  invoiceId: PropTypes.string,
  setInvoiceId: PropTypes.func,
  transactionId: PropTypes.string,
  setTransactionId: PropTypes.func,
  merchantName: PropTypes.string,
  setMerchantName: PropTypes.func,
  setPage: PropTypes.func,
  setShowCalendar: PropTypes.func,
  showCalendar: PropTypes.bool,
  tempDateRange: PropTypes.array,
  handleApply: PropTypes.func,
  handleClear: PropTypes.func,
  handleSelect: PropTypes.func,
  calendarFilterTitle: PropTypes.string,
  handleReset: PropTypes.func,
  invoiceType: PropTypes.string,
};

export default EInvoiceListFilter;
